import styles from './GlobalCopilotOverlay.module.css'
import {ActionList, Button, Link, RelativeTime} from '@primer/react'
import {SkeletonAvatar, SkeletonText} from '@primer/react/experimental'
import {AlertIcon, DotFillIcon, StopIcon} from '@primer/octicons-react'
import {Icon} from '@github-ui/pacer/Icon'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useSSOChangeDetection} from '@github-ui/agent-sessions/hooks/useSSOChangeDetection'
import {useCurrentRepository} from '@github-ui/current-repository'

import type {
  CreateTaskMutationParams,
  CreateTaskParams,
  HydratedTask,
  OptimisticTask,
  UITask,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/agent-sessions/types/task'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {NewAgentTaskForm} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {memo, useCallback, useMemo, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {SessionState} from '@github-ui/agent-sessions/types/session'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {generateTaskPath} from '@github-ui/agent-sessions/utils/generate-task-path'
import {sendEvent} from '@github-ui/hydro-analytics'

export function GlobalCopilotOverlay({onClose}: {onClose?: () => void}) {
  // Detect SSO changes and clear cached tasks
  useSSOChangeDetection()

  const {tasks, isLoading, isError} = useTasks()
  const [failedTasks, setFailedTasks] = useState<CreateTaskMutationParams[]>([])
  const openTasks = tasks.filter(task => !task.pull || (task.pull.state !== 'merged' && task.pull.state !== 'closed'))

  const {createTask} = useCreateTask({
    onError: (_, variables) => {
      setFailedTasks(prev => [{...variables}, ...prev])
    },
  })

  // Memoize the link click handler to prevent unnecessary re-renders
  const handleLinkClick = useCallback(
    (event: React.MouseEvent) => {
      // Only close if it's not a cmd+click (metaKey on Mac) or ctrl+click (ctrlKey on Windows/Linux)
      // These are used to open links in new tabs
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isModifiedClick = event.metaKey || event.ctrlKey
      if (!isModifiedClick && onClose) {
        onClose()
      }
    },
    [onClose],
  )

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <h2 className="f5">Agents</h2>
      </div>
      <div className={styles.content}>
        <NewAgentTaskForm createTask={createTask} eventType="agents_panel" showHeader={false} />
        {isLoading && openTasks.length === 0 ? (
          <TasksLoadingState />
        ) : (
          <>
            {isError ? (
              <TasksErrorState />
            ) : openTasks.length === 0 && failedTasks.length === 0 ? (
              <TasksEmptyState />
            ) : (
              <TasksList
                tasks={openTasks}
                failedTasks={failedTasks}
                setFailedTasks={setFailedTasks}
                onLinkClick={handleLinkClick}
                createTask={createTask}
              />
            )}
            <CopilotDisclosure />
          </>
        )}
      </div>
    </div>
  )
}

// There's an issue with ActionList.LinkItem/Item being unable to handle icon transitions properly unless we memoize the component
const TaskItem = memo(function TaskItem({
  state,
  updatedAt,
  prTitle,
  prState,
  prUrl,
  prNwo,
  prGlobalId,
  prInMergeQueue,
  onLinkClick,
}: {
  state: SessionState
  updatedAt: string
  prTitle: string
  prState: string
  prUrl: string
  prNwo: string
  prGlobalId: string
  prInMergeQueue: boolean
  onLinkClick: (event: React.MouseEvent) => void
}) {
  const iconAttributes = useMemo(
    () => getLeadingVisualIconAttributes(prState, prInMergeQueue ?? false, state),
    [prState, prInMergeQueue, state],
  )

  let taskUrl = prUrl
  if (isFeatureEnabled('copilot_swe_agent_progress_commands')) {
    taskUrl = generateTaskPath(prGlobalId)
  }

  return (
    <ActionList.LinkItem
      className={styles.task}
      href={taskUrl}
      onClick={e => {
        onLinkClick(e)
        sendEvent('agent_sessions.global_agents_menu', {
          target: 'VIEW_TASK_FROM_AGENTS_MENU',
          task_id: prGlobalId,
        })
      }}
    >
      <ActionList.LeadingVisual>
        <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
      </ActionList.LeadingVisual>
      {prTitle}
      <ActionList.Description variant="block" className="no-wrap">
        {prNwo} · <RelativeTime datetime={updatedAt} format="micro" className={styles.taskStartDate} />
      </ActionList.Description>
    </ActionList.LinkItem>
  )
})

function TasksList({
  tasks,
  failedTasks,
  setFailedTasks,
  onLinkClick,
  createTask,
}: {
  tasks: UITask[]
  failedTasks: CreateTaskMutationParams[]
  setFailedTasks: React.Dispatch<React.SetStateAction<CreateTaskMutationParams[]>>
  onLinkClick: (event: React.MouseEvent) => void
  createTask: (params: CreateTaskParams) => void
}) {
  // Separate optimistic tasks from those with pull requests
  const optimisticTasks = useMemo(() => tasks.filter(task => task.isOptimistic), [tasks]) as OptimisticTask[]

  const hydratedTasks = useMemo(() => tasks.filter(task => !task.isOptimistic && task.pull) as HydratedTask[], [tasks])
  const currentRepo = useCurrentRepository()

  const viewAllTasksUrl = useMemo(() => {
    const url = new URL('/copilot/agents', window.location.origin)
    if (currentRepo?.ownerLogin && currentRepo?.name) {
      // Properly encode owner and name to prevent URL injection, preserving the slash
      const repoParam = `${encodeURIComponent(currentRepo.ownerLogin)}/${encodeURIComponent(currentRepo.name)}`
      return `${url.pathname}?repo=${repoParam}`
    }
    return url.pathname
  }, [currentRepo])

  return (
    <ActionList showDividers className={styles.tasksList}>
      <ActionList.Group>
        <ActionList.GroupHeading as="h3" className={styles.tasksListHeading}>
          Recent sessions{' '}
          {tasks && tasks.length !== 0 && (
            <Link
              href={viewAllTasksUrl}
              className={styles.viewAllLink}
              onClick={e => {
                sendEvent('agent_sessions.global_agents_menu', {
                  target: 'VIEW_ALL_TASKS_LINK',
                })
                onLinkClick(e)
              }}
            >
              View all
            </Link>
          )}
        </ActionList.GroupHeading>

        {optimisticTasks.map(task => (
          <OptimisticTaskItem key={task.clientGeneratedId} task={task} />
        ))}
        {failedTasks.map((task: CreateTaskMutationParams) => (
          <FailedTask
            key={task.clientGeneratedId}
            task={task}
            onRetry={() => {
              const {clientGeneratedId, ...taskParams} = task
              createTask(taskParams)
              setFailedTasks(prev => prev.filter(failedTask => failedTask.clientGeneratedId !== clientGeneratedId))
            }}
          />
        ))}
        {hydratedTasks.map(task => {
          return (
            <TaskItem
              key={task.pull.id}
              state={task.latest_session_state}
              updatedAt={task.latest_session_updated_at}
              prTitle={task.pull.title}
              prState={task.pull.state}
              prUrl={task.pull.url}
              prNwo={task.pull.repository_nwo}
              prGlobalId={task.pull.global_relay_id}
              prInMergeQueue={task.pull.inMergeQueue ?? false}
              onLinkClick={onLinkClick}
            />
          )
        })}
      </ActionList.Group>
    </ActionList>
  )
}

function FailedTask({task, onRetry}: {task: CreateTaskMutationParams; onRetry: () => void}) {
  return (
    <ActionList.Item className={styles.task} aria-label={`Failed task: ${task.problemStatement}`}>
      <ActionList.LeadingVisual>
        <StopIcon className="fgColor-danger" />
      </ActionList.LeadingVisual>
      {task.problemStatement}
      <ActionList.Description variant="block" className="no-wrap">
        {task.nwo} · Failed to start
      </ActionList.Description>
      <ActionList.TrailingVisual>
        <Button
          as="a"
          size="small"
          className={styles.retryButton}
          onClick={e => {
            e.stopPropagation()
            onRetry()
          }}
        >
          Retry
        </Button>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
}

function OptimisticTaskItem({task}: {task: OptimisticTask}) {
  return (
    <ActionList.Item className={styles.task} disabled aria-label={`Creating task in ${task.nwo}`}>
      <ActionList.LeadingVisual className={styles.optimisticTaskIcon}>
        <DotFillIcon />
      </ActionList.LeadingVisual>
      <WithShimmerEffect className={styles.shimmeringTaskTitle}>Creating {task.problemStatement}</WithShimmerEffect>
      <ActionList.Description variant="block" className="no-wrap">
        {task.nwo}
      </ActionList.Description>
    </ActionList.Item>
  )
}

function TasksLoadingState() {
  return (
    <ActionList showDividers className={styles.tasksList}>
      <ActionList.Group>
        <ActionList.GroupHeading as="h3" className={styles.tasksListHeading}>
          {/* This matches the width of the "Recent" header in the populated list */}
          {/* We show it as a Skeleton so we don’t flash the text in case it resolves to an empty/error state */}
          {/* eslint-disable-next-line primer-react/no-system-props */}
          <SkeletonText size="bodySmall" width="4.75rem" />
        </ActionList.GroupHeading>
        {Array.from({length: 3}, (_, index) => (
          <ActionList.Item disabled key={index}>
            <ActionList.LeadingVisual>
              <SkeletonAvatar size={16} />
            </ActionList.LeadingVisual>
            {/* eslint-disable-next-line primer-react/no-system-props */}
            <SkeletonText maxWidth="75%" />
            <ActionList.Description variant="block">
              {/* eslint-disable-next-line primer-react/no-system-props */}
              <SkeletonText size="bodySmall" maxWidth="50%" />
            </ActionList.Description>
          </ActionList.Item>
        ))}
      </ActionList.Group>
    </ActionList>
  )
}

function TasksEmptyState() {
  return (
    <MessageState
      heading="Start a new task with Copilot"
      description="Describe your task in natural language. Copilot will work in the background and open a pull request for your review."
      linkHref="https://docs.github.com/copilot/concepts/coding-agent/coding-agent"
      linkText="Learn more about Copilot coding agent"
    />
  )
}

function TasksErrorState() {
  return (
    <MessageState
      showAlertIcon
      heading="Tasks couldn’t be loaded"
      description="Try again or, if the problem persists, contact support."
      linkHref="https://githubstatus.com/"
      linkText="GitHub status"
    />
  )
}

function MessageState({
  showAlertIcon = false,
  heading,
  description,
  linkHref,
  linkText,
}: {
  showAlertIcon?: boolean
  heading: string
  description: string
  linkHref?: string
  linkText?: string
}) {
  return (
    <div className={styles.messageState}>
      <h3 className={styles.messageStateHeading}>
        {showAlertIcon && <AlertIcon className="var(--fgColor-attention)" />}
        {heading}
      </h3>
      <p className={styles.messageStateDescription}>{description}</p>
      {linkHref && linkText && (
        <Link href={linkHref} inline className="d-inline-block">
          {linkText}
        </Link>
      )}
    </div>
  )
}

function CopilotDisclosure() {
  return (
    <div className={styles.copilotDisclosure}>
      <p className={styles.copilotDisclosureText}>
        <Link
          href="https://docs.github.com/copilot/responsible-use/copilot-coding-agent"
          target="_blank"
          rel="noopener noreferrer"
          muted
          inline
        >
          Copilot
        </Link>{' '}
        uses AI. Check for mistakes.
      </p>
    </div>
  )
}

try{ GlobalCopilotOverlay.displayName ||= 'GlobalCopilotOverlay' } catch {}
try{ TaskItem.displayName ||= 'TaskItem' } catch {}
try{ TasksList.displayName ||= 'TasksList' } catch {}
try{ FailedTask.displayName ||= 'FailedTask' } catch {}
try{ OptimisticTaskItem.displayName ||= 'OptimisticTaskItem' } catch {}
try{ TasksLoadingState.displayName ||= 'TasksLoadingState' } catch {}
try{ TasksEmptyState.displayName ||= 'TasksEmptyState' } catch {}
try{ TasksErrorState.displayName ||= 'TasksErrorState' } catch {}
try{ MessageState.displayName ||= 'MessageState' } catch {}
try{ CopilotDisclosure.displayName ||= 'CopilotDisclosure' } catch {}